import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, FileWarning } from "lucide-react";
import { toast } from "sonner";

const toIso = (v) => (v ? new Date(v + ":00+07:00").toISOString() : null);

export default function LateReportForm({ open, onOpenChange, taskId, onDone }) {
  const [cats, setCats] = useState([]);
  const [selected, setSelected] = useState([]);
  const [controllable, setControllable] = useState("internal");
  const [chronology, setChronology] = useState("");
  const [why, setWhy] = useState({ why1: "", why2: "", why3: "", why4: "", why5: "" });
  const [impactLevel, setImpactLevel] = useState("sedang");
  const [financial, setFinancial] = useState("");
  const [recovery, setRecovery] = useState([""]);
  const [newEta, setNewEta] = useState("");
  const [support, setSupport] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/late-categories").then((r) => setCats(r.data)); }, []);

  const toggle = (c) => setSelected((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const submit = async () => {
    if (selected.length === 0) return toast.error("Pilih minimal 1 kategori penyebab");
    if (!chronology.trim()) return toast.error("Kronologi wajib diisi");
    setLoading(true);
    try {
      await api.post("/late-reports", {
        entity_type: "task", entity_id: taskId,
        categories: [selected[0]], additional_categories: selected.slice(1),
        controllable, chronology, root_cause: why, impact_level: impactLevel,
        financial_impact: Number(financial || 0), impact_customer: "",
        recovery_plan: recovery.filter((r) => r.trim()), new_eta: toIso(newEta), support_needed: support,
      });
      toast.success("Laporan Keterlambatan terkirim");
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengirim laporan"); }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto zoa-scroll">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileWarning className="h-5 w-5 text-rose-500" />Laporan Keterlambatan</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Kategori Penyebab (pilih utama + tambahan)</Label>
            <div className="grid grid-cols-2 gap-2">
              {cats.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm cursor-pointer rounded-lg border border-slate-200 px-2.5 py-1.5 hover:bg-slate-50">
                  <Checkbox checked={selected.includes(c)} onCheckedChange={() => toggle(c)} data-testid={`late-cat-${c}`} />
                  <span>{c}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Sifat Penyebab</Label>
            <RadioGroup value={controllable} onValueChange={setControllable} className="flex gap-4">
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="internal" />Dapat dikendalikan</label>
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="eksternal" />Di luar kendali</label>
            </RadioGroup>
          </div>
          <div>
            <Label htmlFor="kron">Kronologi Singkat</Label>
            <Textarea id="kron" value={chronology} onChange={(e) => setChronology(e.target.value)} rows={3} data-testid="late-chronology" />
          </div>
          <div>
            <Label className="mb-1.5 block">Akar Masalah (5 Why — opsional)</Label>
            <div className="space-y-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Input key={n} placeholder={`Why ${n}`} value={why[`why${n}`]}
                  onChange={(e) => setWhy((w) => ({ ...w, [`why${n}`]: e.target.value }))} className="h-9" />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tingkat Dampak</Label>
              <Select value={impactLevel} onValueChange={setImpactLevel}>
                <SelectTrigger data-testid="late-impact-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tinggi">Tinggi</SelectItem>
                  <SelectItem value="sedang">Sedang</SelectItem>
                  <SelectItem value="rendah">Rendah</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estimasi Dampak Finansial (Rp)</Label>
              <Input type="number" value={financial} onChange={(e) => setFinancial(e.target.value)} placeholder="0" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Rencana Pemulihan (jadi langkah checklist)</Label>
            {recovery.map((r, i) => (
              <div key={i} className="flex gap-2 mb-1.5">
                <Input value={r} onChange={(e) => setRecovery((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))}
                  placeholder={`Langkah ${i + 1}`} />
                {recovery.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => setRecovery((arr) => arr.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setRecovery((arr) => [...arr, ""])}><Plus className="h-4 w-4 mr-1" />Tambah langkah</Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Estimasi Selesai Baru</Label>
              <Input type="datetime-local" value={newEta} onChange={(e) => setNewEta(e.target.value)} data-testid="late-new-eta" />
            </div>
            <div>
              <Label>Dukungan dari Atasan</Label>
              <Input value={support} onChange={(e) => setSupport(e.target.value)} placeholder="Opsional" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={submit} disabled={loading} data-testid="late-report-modal-submit-button" className="bg-rose-600 hover:bg-rose-700">
            {loading ? "Mengirim..." : "Kirim Laporan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
